import React, { useState } from "react";
import { Formik, Field } from "formik";
import { connect, useSelector, shallowEqual } from "react-redux";
import { FormattedMessage, injectIntl } from "react-intl";
import * as auth from "../_redux/authRedux";
import { Link } from 'react-router-dom';
import { login, replacePassword } from "../_redux/authCrud";

function Login(props) {
  const { intl } = props;
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useSelector(
    state => ({
      resetPassword: state.auth.resetPassword,
    }),
    shallowEqual,
  );
  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const getInputClasses = (meta, fieldname) => {
    let result = "form-control form-control-solid h-auto py-5 px-6 ";
    if (meta.touched && meta.error) {
      result += " is-invalid";
    }

    if (meta.touched && !meta.error) {
      result += " is-valid";
    }

    return result;
  };

  return (
    <div className="login-form login-signin">
      {/* begin::Head */}
      <div className="text-center mb-10 mb-lg-20">
        {/* https://github.com/formatjs/react-intl/blob/master/docs/Components.md#formattedmessage */}
        {resetPassword.code ?
          <h3 className="font-size-h1">
            Put your new password
          </h3>
          :
          <>
            <h3 className="font-size-h1">
              <FormattedMessage id="AUTH.LOGIN.TITLE" />
            </h3>
            <p className="text-muted font-weight-bold">
              Enter your username and password
            </p>
          </>
        }
      </div>
      {/* end::Head */}

      {/*begin::Form*/}

      <Formik
        initialValues={{
          email: resetPassword.email,
          password: "",
          confirmPassword: "",
        }}
        validate={(values) => {
          const errors = {};

          if (!values.email) {
            // https://github.com/formatjs/react-intl/blob/master/docs/API.md#injection-api
            errors.email = intl.formatMessage({
              id: "AUTH.VALIDATION.REQUIRED_FIELD",
            });
          } else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
          ) {
            errors.email = intl.formatMessage({
              id: "AUTH.VALIDATION.INVALID_FIELD",
            });
          }

          if (!values.password) {
            errors.password = intl.formatMessage({
              id: "AUTH.VALIDATION.REQUIRED_FIELD",
            });
          }
          if (resetPassword.code && !values.confirmPassword) {
            errors.confirmPassword = intl.formatMessage({
              id: "AUTH.VALIDATION.REQUIRED_FIELD",
            });
          }
          if (resetPassword.code &&
            values.password !== values.confirmPassword) {
            errors.confirmPassword = "Password and Confirm Password didn't match.";
          }
          if (resetPassword.code &&
            !values.password.match(/^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,15}$/) &&
            !values.confirmPassword.match(/^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,15}$/)
          ) {
            errors.password = intl.formatMessage({
              id: 'AUTH.VALIDATION.PASSWORD_RULE',
            });
          }

          return errors;
        }}
        onSubmit={(values, { setStatus, setSubmitting, setErrors }) => {
          enableLoading();
          setTimeout(() => {
            if (resetPassword.code) {
              replacePassword(values.email, values.password, resetPassword.code)
                .then(({ data }) => {
                  disableLoading();
                  props.login(data);
                })
                .catch((err) => {
                  disableLoading();
                  setStatus(
                    intl.formatMessage({
                      id: "Your email or reset code are no valid. Please check again.",
                    })
                  );
                  setSubmitting(false);
                })
            } else {
              login(values.email, values.password)
                .then(({ data }) => {
                  disableLoading();
                  props.login(data);
                })
                .catch((err) => {
                  disableLoading();
                  if (err.response?.data) {
                    const authErrorInfo = err.response.data.info;
                    if (authErrorInfo === 'User not found') {
                      setStatus(
                        intl.formatMessage({
                          id: "AUTH.VALIDATION.NO_USER",
                        })
                      );
                    } else if (authErrorInfo === 'Wrong password') {
                      setStatus(
                        intl.formatMessage({
                          id: "AUTH.VALIDATION.INVALID_PASSWORD",
                        })
                      );
                    } else if (authErrorInfo === 'Under review') {
                      setStatus(
                        intl.formatMessage({
                          id: "AUTH.VALIDATION.UNDER_REVIEW",
                        })
                      );
                    } else if (authErrorInfo === 'Too many tries') {
                      setStatus(
                        intl.formatMessage({
                          id: "AUTH.VALIDATION.FREEZE",
                        })
                      );
                    }
                  }
                  setSubmitting(false);
                });
            }
          }, 1000);
        }}
      >
        {({
          values,
          status,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form
            className="form"
            noValidate={true}
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            {status && (
              <div
                role="alert"
                className="mb-10 alert alert-custom alert-light-danger alert-dismissible"
              >
                <div className="alert-text font-weight-bold">{status}</div>
              </div>
            )}

            {!resetPassword.code &&
              <div className="form-group">
                <Field
                  name="email"
                  type="email"
                  placeholder="Email"
                  label="Email"
                >
                  {({ field, form, meta }) => (
                    <div>
                      <input
                        type="email"
                        {...field}
                        className={`${getInputClasses(meta)}`}
                        placeholder="Enter Email"
                      />
                      {meta.touched && meta.error && (
                        <div className="error invalid-feedback">{meta.error}</div>
                      )}
                    </div>
                  )}
                </Field>
              </div>
            }

            <div className="form-group">
              <Field
                name="password"
                type="password"
                placeholder="Password"
                label="Password"
              >
                {({ field, form, meta }) => (
                  <div>
                    <input
                      type="password"
                      {...field}
                      className={`${getInputClasses(meta)}`}
                      placeholder="Enter Password"
                    />
                    {meta.touched && meta.error && (
                      <div className="error invalid-feedback">{meta.error}</div>
                    )}
                  </div>
                )}
              </Field>
            </div>
            {resetPassword.code && resetPassword.email ?
              <div>
                <div className='form-group'>
                  <Field
                    type='password'
                    name='confirmPassword'
                    placeholder='Confirm Password'
                    label='Confirm Password'
                  >
                    {({ field, form, meta }) => (
                      <div>
                        <input
                          type='password'
                          {...field}
                          className={`${getInputClasses(meta)}`}
                          placeholder='Confirm Password'
                        />
                        {meta.touched && meta.error && (
                          <div className='error invalid-feedback'>{meta.error}</div>
                        )}
                      </div>
                    )}
                  </Field>
                </div>
              </div> : null}

            {/* begin::Actions */}
            <div className="form-group d-flex flex-wrap justify-content-end align-items-center">
              {/* <Link
                to="/auth/forgot-password"
                className="text-dark-50 text-hover-primary my-3 mr-2"
                id="kt_login_forgot"
              >
                <FormattedMessage id="AUTH.GENERAL.FORGOT_BUTTON" />
              </Link> */}
              <Link to="/auth/forgot-password" className='font-weight-bold ml-2 mr-10'>
                Forgot your password?
              </Link>
              <button
                id="kt_login_signin_submit"
                type="submit"
                disabled={isSubmitting}
                className={`btn btn-dark font-weight-bold px-9 py-4 my-3`}
              >
                <span>Sign In</span>
                {loading && (
                  <span className="ml-3 spinner spinner-white"></span>
                )}
              </button>
            </div>
            {/* end::Actions */}
          </form>
        )}
      </Formik>
      {/*end::Form*/}
    </div >
  );
}

export default injectIntl(connect(null, auth.actions)(Login));
